import React, { useRef, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { Alert, Button, DatePicker, Input, Modal, Select, Space, Table, Tag } from 'antd';

export const staffStatusOptions = [
    { value: 'unconfirmed', label: '未確認' },
    { value: 'active', label: '正常' },
    { value: 'retired', label: '退休' },
    { value: 'withdrawn', label: '退學' },
    { value: 'transferred', label: '調職／調離' },
];
const label = code => staffStatusOptions.find(item => item.value === (code || 'unconfirmed'))?.label || code;
const dateLabel = value => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—';
export const staffStatusRequestId = () => {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export default function StaffStatus({ record, apiRoute, readonly = false, onChanged = () => {} }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [state, setState] = useState(null);
    const [history, setHistory] = useState([]);
    const [canEdit, setCanEdit] = useState(false);
    const [code, setCode] = useState('unconfirmed');
    const [reason, setReason] = useState('');
    const [effective, setEffective] = useState(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const pending = useRef(null);
    if (!record?.staff_id || !record?.user_id) return null;

    const load = async () => {
        setLoading(true); setError(''); setNotice(''); setCanEdit(false); setState(null);
        pending.current = null;
        try {
            const { data } = await axios.get(apiRoute, { params: {
                staff_status_history: 1, staff_id: record.staff_id, user_id: record.user_id,
            } });
            if (data.status !== 'success' || !data.data) throw new Error(data.message || '無法讀取人員狀態');
            setState(data.data); setHistory(data.history || []); setCanEdit(data.can_edit === true);
            setCode(data.data.staff_status_code || 'unconfirmed'); setReason(''); setEffective(dayjs());
        } catch (e) { setError(e.response?.data?.message || e.message || '無法讀取人員狀態'); }
        finally { setLoading(false); }
    };
    const save = async () => {
        setError(''); setNotice('');
        if (!pending.current) {
            if (!effective?.isValid() || effective.isAfter(dayjs())) { setError('生效時間須為現在或過去'); return; }
            if (code === 'withdrawn' && !reason.trim()) { setError('退學原因必填'); return; }
            if (!window.crypto?.getRandomValues) { setError('瀏覽器無法產生異動識別碼，請使用支援的瀏覽器重試'); return; }
            pending.current = { staff_id: record.staff_id, user_id: record.user_id, staff_status_change: {
                code, reason: reason.trim(), effective_at: effective.toISOString(),
                expected_version: Number(state.staff_status_version || 0), request_id: staffStatusRequestId(),
            } };
        }
        setLoading(true);
        try {
            const { data } = await axios.patch(apiRoute, [pending.current]);
            if (data.status !== 'success') throw new Error(data.message || '狀態儲存失敗');
            setState(data.data);
            onChanged(data.data);
            await load();
            setNotice('人員狀態已儲存，僅供顯示與紀錄。');
        } catch (e) {
            setError(e.response?.data?.message || e.message || '狀態儲存未確認，請重試原異動或重新載入');
            // A conflict requires a deliberate reload; a network failure retries the same request.
            if (e.response?.status === 409) setCanEdit(false);
        } finally { setLoading(false); }
    };
    const editing = !readonly && canEdit && state;
    const statusCode = state?.staff_status_code || record.staff_status_code;
    const openStatus = event => {
        event.stopPropagation();
        setOpen(true);
        load();
    };
    return <>
        <Tag color={statusCode === 'withdrawn' ? 'red' : undefined}
            role="button" tabIndex={0} aria-label={`人員狀態：${label(statusCode)}`}
            style={{ cursor: 'pointer' }} onClick={openStatus}
            onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openStatus(event);
                }
            }}>
            {label(statusCode)}
        </Tag>
        <Modal title={`人員狀態：${record.staff_name || ''}`} open={open} onCancel={() => setOpen(false)}
            footer={<Space><Button disabled={loading} onClick={load}>重新載入</Button>
                {editing && <Button type="primary" loading={loading} onClick={save}>
                    {pending.current ? '重試原異動' : '儲存人員狀態'}
                </Button>}
                <Button disabled={loading} onClick={() => setOpen(false)}>關閉</Button></Space>}>
            <p>人員狀態僅供顯示與紀錄，不影響借書、還書、續借或預約；借閱資格依原有過期日及停權規則判斷。</p>
            {error && <Alert type="error" showIcon message={error} />}
            {notice && <Alert type="success" showIcon message={notice} />}
            {state && <p>目前：{label(state.staff_status_code)}，生效時間：{dateLabel(state.staff_status_effective_at)}</p>}
            {editing && <Space direction="vertical" style={{ width: '100%' }}>
                <label>人員狀態<Select aria-label="人員狀態" value={code} options={staffStatusOptions}
                    disabled={loading || !!pending.current} onChange={setCode} style={{ width: '100%' }} /></label>
                <label>生效時間<DatePicker aria-label="生效時間" showTime value={effective} onChange={setEffective}
                    disabled={loading || !!pending.current} style={{ width: '100%' }} /></label>
                <label>異動原因{code === 'withdrawn' ? '（必填）' : ''}<Input.TextArea aria-label="異動原因"
                    value={reason} maxLength={1000} showCount disabled={loading || !!pending.current}
                    onChange={e => setReason(e.target.value)} /></label>
            </Space>}
            <p>最近 100 筆異動紀錄</p>
            <Table size="small" rowKey="staff_status_history_id" loading={loading} pagination={false} dataSource={history}
                columns={[
                    { title: '異動', key: 'status', render: (_, row) => `${label(row.old_status_code)} → ${label(row.new_status_code)}` },
                    { title: '生效時間', dataIndex: 'effective_at', render: dateLabel },
                    { title: '原因', dataIndex: 'reason' },
                    { title: '操作者 ID', dataIndex: 'changed_by' },
                    { title: '記錄時間', dataIndex: 'changed_at', render: dateLabel },
                ]} />
        </Modal>
    </>;
}
